import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { AgentQuotaDTO, AgentQuotaFormValues } from '../../types';
import { useAgentQuotaAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';
import { UnverifiedBanner } from '../Shared/UnverifiedBanner';

const EMPTY_FORM: AgentQuotaFormValues = { QuotaTypeID: 0, Value: 0 };

/**
 * Restored Quotas tab (Phase 3 of the Agent-editor sub-entity restoration — see
 * TemplatesPanel.tsx/RulesPanel.tsx/HooksPanel.tsx, this panel's direct structural template).
 * Real entity (AIAgent_AgentQuotas) confirmed by reading AgentQuota.cs directly: just a required
 * QuotaTypeID FK + a required decimal Value — no QuotaName/QuotaPeriod enum/CurrentUsage rollup,
 * exactly as the task briefing hypothesized (route drift was found instead — see
 * agentQuotaAdminApiClient.ts). No lookup controller was found for QuotaTypeID, so it renders as
 * a plain required number input, same treatment RulesPanel.tsx gives AgentRuleTypeID.
 */
export function QuotasPanel({ agentId }: { agentId: number }) {
  const api = useAgentQuotaAdminApiClient();
  const [quotas, setQuotas] = useState<AgentQuotaDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AgentQuotaDTO | 'new' | null>(null);
  const [form, setForm] = useState<AgentQuotaFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgentQuotaDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getQuotasByAgent(agentId);
    setQuotas(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (quota: AgentQuotaDTO | 'new') => {
    setEditing(quota);
    setError(null);
    setForm(quota === 'new' ? EMPTY_FORM : { QuotaTypeID: quota.QuotaTypeID, Value: quota.Value });
  };

  const handleSave = async () => {
    if (!form.QuotaTypeID) {
      setError('A quota type is required.');
      return;
    }
    setSaving(true);
    setError(null);
    const result = editing === 'new' ? await api.createQuota(agentId, form) : await api.updateQuota((editing as AgentQuotaDTO).AgentQuotaID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    } else {
      setError(result.error?.message ?? 'Failed to save.');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteQuota(pendingDelete.AgentQuotaID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setQuotas((prev) => prev.filter((q) => q.AgentQuotaID !== pendingDelete.AgentQuotaID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <UnverifiedBanner entityName="Quotas" />
        <div className="aaa-form-group">
          <label>Quota Type ID *</label>
          <input
            type="number"
            value={form.QuotaTypeID || ''}
            onChange={(e) => setForm((f) => ({ ...f, QuotaTypeID: e.target.value ? parseInt(e.target.value, 10) : 0 }))}
          />
        </div>
        <div className="aaa-form-group">
          <label>Value *</label>
          <input
            type="number"
            step={0.01}
            value={form.Value}
            onChange={(e) => setForm((f) => ({ ...f, Value: e.target.value ? parseFloat(e.target.value) : 0 }))}
          />
        </div>
        {error && <div className="aaa-editor-error aaa-form-group-full">{error}</div>}
        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Quota'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <UnverifiedBanner entityName="Quotas" />
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Quota
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && quotas.length === 0 && <div className="aaa-entity-dropdown-empty">No quotas yet.</div>}
      <div className="aaa-entity-grid">
        {quotas.map((quota) => (
          <div key={quota.AgentQuotaID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>Quota Type #{quota.QuotaTypeID}</strong>
            </div>
            <p>Value: {quota.Value}</p>
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(quota)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(quota)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Quota"
        message={`Delete quota (type #${pendingDelete?.QuotaTypeID})?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
